import { createFileRoute, Outlet, useMatches } from '@tanstack/react-router';
import { useMemo, useState } from 'react';
import styled from 'styled-components';
import api from '@/api';
import { Sidebar } from '@/courses/course-sidebar/Sidebar';
import { CourseContext } from '~/contexts/courseContext';
import { useCurrentUser } from '~/hooks/useCurrentUser';
import { useWindowWidth } from '~/hooks/useWindowWidth';

export const Route = createFileRoute('/_app/_course')({
  component: CourseLayout,
});

declare module '@tanstack/react-router' {
  interface RouteContext {
    '/_app/_course': typeof CourseContext;
  }
}

function CourseLayout() {
  const windowWidth = useWindowWidth();
  const [selectedLessonSlug, setSelectedLessonSlug] = useState('');
  const [expandedLessons, setExpandedLessons] = useState<string[]>([]);
  const [selectedTopicSlug, setSelectedTopicSlug] = useState('');
  const { isStudent, me } = useCurrentUser();
  const language = me?.language ?? 'EN';

  const toggleLesson = (slug: string) => {
    setExpandedLessons((prev) =>
      prev.includes(slug) ? prev.filter((s) => s !== slug) : [...prev, slug],
    );
  };

  // Derive course from the active child route's params
  const matches = useMatches();
  const lessonSlug =
    matches.find((m) => m.routeId === '/_app/_course/lessons/$lessonSlug')
      ?.params.lessonSlug ??
    matches.find((m) => m.routeId === '/_app/_course/lessons/$lessonSlug_/quiz')
      ?.params.lessonSlug;
  const topicSlug = matches.find(
    (m) => m.routeId === '/_app/_course/topic/$topicSlug',
  )?.params.topicSlug;

  const { data: resolvedLesson } = api.lessonBySlug.useQuery(
    { path: { slug: lessonSlug ?? '' }, query: { language } },
    { enabled: lessonSlug != null },
  );

  // `topic_by_slug` returns ids and slugs only, and is ungated for exactly this
  // reason: the sidebar still renders when the topic BODY read (`topic_detail`,
  // which is access-gated) answers 403. Sourcing the layout's ids from the body
  // read left the whole shell blank for a refused student.
  const { data: resolvedTopic } = api.topicBySlug.useQuery(
    { path: { slug: topicSlug ?? '' }, query: { language } },
    { enabled: topicSlug != null },
  );

  const lessonId = resolvedLesson?.id ?? resolvedTopic?.lesson_id;

  const { data: lessonDetail } = api.lessonDetail.useQuery(
    { path: { lesson_id: lessonId ?? 0 } },
    { enabled: lessonId != null },
  );

  const courseId = resolvedTopic?.course_id ?? lessonDetail?.course_id;

  const { data: courseDetail } = api.courseDetail.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null },
  );

  const { data: lessonsData } = api.courseLessons.useQuery(
    { path: { course_id: courseId ?? 0 }, query: { limit: 50 } },
    { enabled: courseId != null },
  );

  const { data: courseProgress } = api.courseProgress.useQuery(
    { path: { course_id: courseId ?? 0 } },
    { enabled: courseId != null && isStudent },
  );

  const { completedSteps, totalSteps, progressPercentage } = useMemo(() => {
    if (!courseProgress)
      return { completedSteps: 0, totalSteps: 0, progressPercentage: 0 };
    const values = Object.values(courseProgress);
    const total = values.reduce((s, lp) => s + lp.topics_total, 0);
    const completed = values.reduce((s, lp) => s + lp.topics_completed, 0);
    return {
      completedSteps: completed,
      totalSteps: total,
      progressPercentage: total > 0 ? Math.round((completed / total) * 100) : 0,
    };
  }, [courseProgress]);

  const lessons = useMemo(
    () =>
      (lessonsData?.data ?? []).map((lesson) => ({
        id: lesson.id,
        slug: lesson.slug,
        title: lesson.title,
        topicsCount: lesson.topics_count,
        hasFinalQuiz: lesson.has_final_quiz,
        completed: courseProgress
          ? (courseProgress[String(lesson.id)]?.completed ?? false)
          : undefined,
      })),
    [lessonsData, courseProgress],
  );

  const contextValue = {
    setSelectedLessonSlug,
    setExpandedLessons,
    selectedTopicSlug,
    setSelectedTopicSlug,
  };

  return (
    <CourseContext.Provider value={contextValue}>
      <Page>
        <Layout>
          {windowWidth > 789 && (
            <Sidebar
              title={courseDetail?.title ?? ''}
              courseSlug={courseDetail?.slug ?? ''}
              lessons={lessons}
              lessonProgressMap={courseProgress ?? {}}
              selectedLessonSlug={selectedLessonSlug}
              expandedLessons={expandedLessons}
              onLessonSelect={setSelectedLessonSlug}
              onToggleLesson={toggleLesson}
              selectedTopicSlug={selectedTopicSlug}
              isStudent={isStudent}
              courseProgress={progressPercentage}
              completedSteps={completedSteps}
              totalSteps={totalSteps}
              memberIdForCourseStudy={me?.id?.toString()}
            />
          )}
          <Outlet />
        </Layout>
      </Page>
    </CourseContext.Provider>
  );
}

const Page = styled.div`
  background: var(--bg-default);
  height: calc(100vh - 120px);
  margin: 0 auto;
  overflow: hidden;

  @media (max-width: 789px) {
    height: auto;
    max-width: 100%;
    overflow: visible;
  }
`;

const Layout = styled.div`
  display: flex;
  height: 100%;
  margin: 0 auto;
  max-width: 1270px;
  overflow: hidden;

  @media (max-width: 789px) {
    height: auto;
    overflow: visible;
  }
`;
